import { useEffect, useState } from 'react';
import { TrendingUp, DollarSign, Users, Calendar, Info } from 'lucide-react';

const API = import.meta.env.VITE_API_URL;

export default function Dashboard() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/affiliate-portal/dashboard`, { credentials: 'include' })
      .then((r) => r.json() as Promise<{ success: boolean; data: any }>)
      .then((j) => {
        if (j.success) setData(j.data);
        setLoading(false);
      });
  }, []);

  if (loading) return <div className="text-center text-slate-500 dark:text-slate-400 py-8">Loading...</div>;
  if (!data) return <div className="text-center text-slate-500 dark:text-slate-400 py-8">Unable to load dashboard.</div>;

  const forecast = data.forecast;
  const projectedMonthly = forecast ? Number(forecast.projected_monthly_total || 0) : 0;
  const overrideTotal = forecast ? Number(forecast.override_total || 0) : 0;

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Dashboard</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard icon={DollarSign} label="Running Balance" value={`$${data.running_balance.toFixed(2)}`} color={data.running_balance < 0 ? 'red' : 'green'} subtext="Earned minus paid" />
        <StatCard icon={TrendingUp} label="YTD Earned" value={`$${data.ytd_earned.toFixed(2)}`} subtext={`Year-to-date`} />
        <StatCard icon={Calendar} label="YTD Paid" value={`$${data.ytd_paid.toFixed(2)}`} subtext={`${new Date().getUTCFullYear()}`} />
        <StatCard icon={Users} label="Active Referrals" value={data.active_referrals} subtext={`${data.total_referrals} total`} />
      </div>

      {/* Forward-looking monthly forecast */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900 dark:text-white">Projected Monthly Commission</h3>
        </div>
        <div className="text-3xl font-bold text-blue-700 dark:text-blue-400 font-mono">
          ${projectedMonthly.toFixed(2)}
          <span className="text-base font-sans font-normal text-slate-400"> /mo</span>
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
          {forecast
            ? `${forecast.tier_rate}% rate · ${forecast.active_referral_count} active referral${forecast.active_referral_count === 1 ? '' : 's'}${overrideTotal > 0 ? ` · incl. $${overrideTotal.toFixed(2)} override` : ''}`
            : 'N/A'}
        </div>
        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-950/40 rounded text-xs text-blue-800 dark:text-blue-300 flex items-start gap-2">
          <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            Estimate assuming your active referrals stay on their current plans. Commissions vest and pay out the
            month after each customer's renewal.
          </span>
        </div>
      </div>

      {/* Lifetime stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Lifetime Totals</h3>
          <div className="space-y-2 text-sm">
            <Row label="Total earned (all time)" value={`$${data.lifetime_earned.toFixed(2)}`} />
            <Row label="Total paid (all time)" value={`$${data.lifetime_paid.toFixed(2)}`} />
            <Row label="Periods on record" value={data.period_count} />
          </div>
        </div>
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-6">
          <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Last Payment</h3>
          {data.last_payment ? (
            <div className="space-y-2 text-sm">
              <Row label="Amount" value={`$${Number(data.last_payment.amount).toFixed(2)}`} />
              <Row label="Method" value={data.last_payment.method} />
              <Row label="Paid on" value={new Date(data.last_payment.paid_at).toLocaleDateString()} />
            </div>
          ) : (
            <p className="text-slate-500 dark:text-slate-400 text-sm">No payments yet. Once commissions are due, they will appear in the Payments tab.</p>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, subtext, color = 'blue' }: any) {
  const colors: Record<string, string> = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    red: 'text-red-600',
  };
  return (
    <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-slate-500 dark:text-slate-400">{label}</span>
        <Icon className={`w-4 h-4 ${colors[color]}`} />
      </div>
      <div className={`text-xl font-bold font-mono ${colors[color]}`}>{value}</div>
      {subtext && <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subtext}</div>}
    </div>
  );
}

function Row({ label, value }: { label: string; value: any }) {
  return (
    <div className="flex justify-between border-b pb-1">
      <span className="text-slate-600 dark:text-slate-300">{label}</span>
      <span className="font-medium font-mono">{value}</span>
    </div>
  );
}
